import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
// TODO: check how to prevent tests from failing
// while using the absolute path for components/editDonor
import AddressInputs from '@/components/editDonor/AddressInputs';
import Labels from '@/constants/ui/Labels';
import translate from '@/lib/intl/translate';
import {
  mapAddresses,
  mapEmails,
  mapPhoneNumbers,
  mapWebsite,
} from '@/lib/mappers/donors-data';
import { ConstituentSectionTypes } from '@/propTypes';
import { formatDate, isValidDate } from '@/utils/dates';
import formatWebAddress from '@/utils/formatWebAddress';
import { isValidUrl } from '@/utils/urls';
import EditDonorPhoneInput from '../../components/editDonor/EditDonorPhoneInput';
import {
  BirthdayIcon,
  BuildingIcon,
  EditBuildingIcon,
  EditEmployeeIdIcon,
  EditWebIcon,
  EditWorkPhoneIcon,
  EmailIcon,
  EmployeeIdIcon,
  HomeIcon,
  IndustryIcon,
  JobIcon,
  JobPositionIcon,
  PhoneIcon,
  RedPhoneIcon,
  WebIcon,
  WorkPhoneIcon,
} from './donor-icons';

const EID_FIRST_PART_LENGTH = 2;
const EID_NUM_LENGTH = 9;

const {
  EMPLOYEES,
  CONNECTIONS,
  HOUSEHOLD,
  AFFILIATIONS,
  CONTACT_INFORMATION,
  WORK_INFORMATION,
  PERSONAL_INFORMATION,
  COMPANY_INFORMATION,
} = ConstituentSectionTypes;

/**
  Take a look on mapDonorData params from lib/mappers
  if you want to check which fields to use for configs
*/

const PHONE_LABEL_ICON_MAPPER = {
  [Labels.HOME]: RedPhoneIcon,
  [Labels.WORK]: WorkPhoneIcon,
  [Labels.PERSONAL]: WorkPhoneIcon,
  [Labels.MOBILE]: PhoneIcon,
  [Labels.OFFICE]: WorkPhoneIcon,
  [Labels.FAX]: WorkPhoneIcon,
};

// used for showing dob in view mode
const formatDateOfBirth = (value) => {
  try {
    let dob = formatDate(value, 'MMM dd');
    const year = formatDate(value, 'yyyy');

    if (year) {
      dob = `${dob}, ${year}`;
    }

    return dob;
  } catch (e) {
    return value;
  }
};

const formatEmployeeId = (value = '') => {
  if (
    value?.length > EID_FIRST_PART_LENGTH &&
    value?.length <= EID_NUM_LENGTH
  ) {
    const [firstNum, secondNum, ...rest] = value.split('');

    return `${firstNum}${secondNum}-${rest.join('')}`;
  }

  return value;
};

// used in forms
const mapBirthdayValue = (value) => {
  try {
    // formats a birthday in case data is entered fully
    return value?.length >= 10 ? formatDate(value, 'MM/dd/yyyy') : value;
  } catch (e) {
    // eslint-disable-next-line
    console.warn(`couldn't format the date: ${value}`);
    return value;
  }
};

// Something is wrong with this import, wondering if it is some babel cache / strangeness
// TODO revisit
const isNotEmpty = (value) => Boolean(value);

/**
 * Sections with configs for generation View and Edit Sections
 * of the Donor Page
 *
 * View Sections (used in ViewFieldsColumn)
 * - key*
 * - path*
 * - fieldToExtract - a field to extract after extracting data by path
 * - formatValue - formats an extracted value if necessary (used in src/lib/mappers)
 * - icon
 * - iconMapper - is used to map an icon by field label
 * - label
 * - labels - to filter data based on it (used in src/lib/mappers)
 * - mapper - for mapping field data (used in ViewFieldsColumn)
 * - multiline - allows or not to show data on multiple lines (necessary for rendering addresses)
 * - noDataLabel - a label to show in case a field is empty
 *
 *
 * Popup Sections (used in SectionsGenerator)
 * - key*
 * - path*
 * - Component - a component to render as an input.
 *   Is required in case useMultipleInputs prop is true.
 * - defaultValue - a default value for rendering a new input
 * - errorMessage - an error message to show in case validation is failed
 * - fieldToExtract - a field to extract after extracting data by path (used in MultipleInputs)
 * - icon
 * - label
 * - labels - for rendering a selector with types (used in MultipleInputs)
 * - required - if a value should be required or not
 * - useMultipleInputs - a flag for rendering inputs using MultipleInputs
 * - usePrimary - used in MultipleInputs to render or nor PrimaryCheckbox
 * - validate - a function which return if a value is valid or not
 * - valueMapper - a function which returns a formatted value for input
 */

const DEFAULT_PHONE_CONFIG = {
  key: 'PHONES',
  icon: WorkPhoneIcon,
  iconMapper: PHONE_LABEL_ICON_MAPPER,
  path: 'phones',
  fieldToExtract: 'number',
  mapper: mapPhoneNumbers,
  noDataLabel: translate('ADD_PHONE'),
  editField: 'phones.0.number',
};

const DEFAULT_EMAIL_CONFIG = {
  key: 'EMAILS',
  icon: EmailIcon,
  path: 'emails',
  fieldToExtract: 'email',
  noDataLabel: translate('ADD_EMAIL'),
  mapper: mapEmails,
  editField: 'emails.0.email',
};

const DEFAULT_ADDRESS_CONFIG = {
  key: 'ADDRESSES',
  icon: BuildingIcon,
  path: 'addresses',
  noDataLabel: translate('ADD_ADDRESS'),
  mapper: mapAddresses,
  multiline: true,
  editField: 'address',
};

const EDIT_ADDRESS_DEFAULT_VALUE = {
  country: '',
  label: Labels.PERSONAL,
  line1: '',
  unit: '',
  city: '',
  state: '',
  postalCode: '',
};

const DONOR_FIELDS_CONFIG = {
  USER_BIRTHDAY: {
    key: 'USER_BIRTHDAY',
    icon: BirthdayIcon,
    path: 'dob',
    noDataLabel: translate('ADD_BIRTHDAY'),
    label: translate('BIRTHDAY_PLACEHOLDER'),
    formatValue: formatDateOfBirth,
    valueMapper: mapBirthdayValue,
    validate: (value) => isValidDate(value, 'MM/dd/yyyy'),
    errorMessage: translate('INVALID_DATE_FORMAT_MESSAGE'),
    editField: '_birthday',
  },
  USER_HOME_ADDRESS: {
    ...DEFAULT_ADDRESS_CONFIG,
    icon: HomeIcon,
  },
  CONTACT_ADDRESS: {
    ...DEFAULT_ADDRESS_CONFIG,
    icon: HomeIcon,
  },
  USER_WORK_ADDRESS: {
    ...DEFAULT_ADDRESS_CONFIG,
    icon: BuildingIcon,
  },

  // orgs specific
  ORG_INDUSTRY: {
    key: 'ORG_INDUSTRY',
    icon: IndustryIcon,
    label: translate('DONOR_INDUSTRY_LABEL'),
    noDataLabel: translate('ADD_INDUSTRY'),
    path: 'industry',
  },
  ORG_ADDRESS: DEFAULT_ADDRESS_CONFIG,

  // common
  DISPLAY_NAME: {
    key: 'DISPLAY_NAME',
    label: translate('DISPLAY_NAME_LABEL'),
    path: 'displayName',
  },
  WEBSITE: {
    key: 'WEBSITE',
    icon: WebIcon,
    path: 'website',
    label: translate('WEBSITE_LABEL'),
    mapper: mapWebsite,
    formatValue: formatWebAddress,
    noDataLabel: translate('ADD_WEBSITE'),
    validate: isValidUrl,
    errorMessage: translate('INVALID_URL_FORMAT_MESSAGE'),
  },
  LABEL: {
    key: 'LABEL',
    path: 'label',
    formatValue: capitalize,
  },
  EMAILS: {
    ...DEFAULT_EMAIL_CONFIG,
    icon: null,
    noDataLabel: '',
  },
  PHONES: {
    ...DEFAULT_PHONE_CONFIG,
    icon: null,
    iconMapper: {},
    noDataLabel: '',
  },
  DESCRIPTION: {
    key: 'DESCRIPTION',
    label: translate('TYPE_LABEL'),
    path: 'description',
    required: true,
    formatValue: capitalize,
  },
};

// CommonInformation related configs
const USER_COMMON_INFO_FIRST_COL_CONFIG = [
  DONOR_FIELDS_CONFIG.USER_HOME_ADDRESS,
  DONOR_FIELDS_CONFIG.USER_BIRTHDAY,
];

const ORG_COMMON_INFO_FIRST_COL_CONFIG = [
  DONOR_FIELDS_CONFIG.ORG_INDUSTRY,
  DONOR_FIELDS_CONFIG.ORG_ADDRESS,
];

// DonorConnectionSection related configs
const USER_CONNECTION_CARD_CONFIG = [
  DONOR_FIELDS_CONFIG.DISPLAY_NAME,
  DONOR_FIELDS_CONFIG.LABEL,
  DONOR_FIELDS_CONFIG.EMAILS,
  DONOR_FIELDS_CONFIG.PHONES,
];

// DonorConnectionSection related configs
const ORG_CONNECTION_CARD_CONFIG = [
  DONOR_FIELDS_CONFIG.DISPLAY_NAME,
  DONOR_FIELDS_CONFIG.DESCRIPTION,
  {
    ...DONOR_FIELDS_CONFIG.WEBSITE,
    icon: null,
    noDataLabel: '',
  },
  DONOR_FIELDS_CONFIG.EMAILS,
  DONOR_FIELDS_CONFIG.PHONES,
];

// DonorWorkAndContactsSection related configs
const ORG_CONTACTS_FIRST_COL_CONFIG = [
  {
    ...DONOR_FIELDS_CONFIG.LABEL,
    icon: JobPositionIcon,
  },
  DONOR_FIELDS_CONFIG.CONTACT_ADDRESS,
];

const ORG_CONTACTS_SECOND_COL_CONFIG = [
  {
    ...DONOR_FIELDS_CONFIG.EMAILS,
    icon: EmailIcon,
  },
  {
    ...DONOR_FIELDS_CONFIG.PHONES,
    iconMapper: PHONE_LABEL_ICON_MAPPER,
  },
  DONOR_FIELDS_CONFIG.WEBSITE,
  DONOR_FIELDS_CONFIG.USER_BIRTHDAY,
];

const USER_WORK_INFO_FIRST_COL_CONFIG = [
  {
    ...DONOR_FIELDS_CONFIG.DESCRIPTION,
    icon: JobPositionIcon,
  },
  DONOR_FIELDS_CONFIG.USER_WORK_ADDRESS,
];

const USER_WORK_INFO_SECOND_COL_CONFIG = [
  DEFAULT_EMAIL_CONFIG,
  DEFAULT_PHONE_CONFIG,
  DONOR_FIELDS_CONFIG.WEBSITE,
];

const DONOR_SECTION_CONFIGS = {
  [HOUSEHOLD]: {
    key: HOUSEHOLD,
    title: translate('DONOR_HOUSEHOLD_LABEL'),
    actionLabel: translate('DONOR_ADD_HOUSEHOLD_LABEL'),
    config: USER_CONNECTION_CARD_CONFIG,
  },
  [CONNECTIONS]: {
    key: CONNECTIONS,
    title: translate('DONOR_CONNECTIONS_LABEL'),
    actionLabel: translate('DONOR_ADD_CONNECTION_LABEL'),
    config: USER_CONNECTION_CARD_CONFIG,
  },
  [EMPLOYEES]: {
    key: EMPLOYEES,
    title: translate('DONOR_EMPLOYEES_LABEL'),
    actionLabel: translate('DONOR_ADD_EMPLOYEE_LABEL'),
    config: USER_CONNECTION_CARD_CONFIG,
  },
  [AFFILIATIONS]: {
    key: AFFILIATIONS,
    title: translate('DONOR_AFFILIATIONS_LABEL'),
    actionLabel: translate('DONOR_ADD_AFFILIATION_LABEL'),
    config: ORG_CONNECTION_CARD_CONFIG,
  },
  [CONTACT_INFORMATION]: {
    key: CONTACT_INFORMATION,
    title: translate('DONOR_CONTACTS_LABEL'),
    actionLabel: translate('DONOR_ADD_CONTACT_LABEL'),
    primaryLabel: translate('DONOR_PRIMARY_CONTACT_LABEL'),
    firstColConfig: ORG_CONTACTS_FIRST_COL_CONFIG,
    secondColConfig: ORG_CONTACTS_SECOND_COL_CONFIG,
  },
  [WORK_INFORMATION]: {
    key: WORK_INFORMATION,
    title: translate('DONOR_WORK_INFORMATION'),
    actionLabel: translate('DONOR_ADD_WORK_LABEL'),
    primaryLabel: translate('DONOR_PRIMARY_WORK_LABEL'),
    firstColConfig: USER_WORK_INFO_FIRST_COL_CONFIG,
    secondColConfig: USER_WORK_INFO_SECOND_COL_CONFIG,
  },
  [PERSONAL_INFORMATION]: {
    key: PERSONAL_INFORMATION,
    title: translate('DONOR_PERSONAL_INFORMATION'),
    firstColConfig: USER_COMMON_INFO_FIRST_COL_CONFIG,
  },
  [COMPANY_INFORMATION]: {
    key: COMPANY_INFORMATION,
    title: translate('DONOR_COMPANY_INFORMATION'),
    firstColConfig: ORG_COMMON_INFO_FIRST_COL_CONFIG,
  },
};

const PHONE_NUMBER_COMMON_PROPS = {
  Component: EditDonorPhoneInput,
  icon: EditWorkPhoneIcon,
  path: 'phones',
  useMultipleInputs: true,
  useIndexes: false,
};

const EMPLOYEE_PROFILE_FIELDS_CONFIG = {
  USER_FIRST_NAME: {
    key: 'USER_FIRST_NAME',
    label: translate('FIRST_NAME_LABEL'),
    path: 'firstName',
    required: true,
    validate: isNotEmpty,
  },
  USER_LAST_NAME: {
    key: 'USER_LAST_NAME',
    label: translate('LAST_NAME_LABEL'),
    path: 'lastName',
  },
  USER_DISPLAY_NAME: DONOR_FIELDS_CONFIG.DISPLAY_NAME,
  USER_TITLE: {
    key: 'USER_TITLE',
    icon: JobIcon,
    label: translate('TITLE_LABEL'),
    path: 'jobTitle',
  },
  USER_WORK_EMAIL: {
    key: 'USER_WORK_EMAIL',
    icon: EmailIcon,
    label: translate('WORK_EMAIL_ADDRESS_LABEL'),
    path: 'workEmail',
    required: true,
    disabled: true,
    validate: isNotEmpty,
  },
  USER_PHONE_NUMBER: {
    key: 'USER_PHONE_NUMBER',
    icon: WorkPhoneIcon,
    iconMapper: PHONE_LABEL_ICON_MAPPER,
    path: 'phones',
    fieldToExtract: 'number',
    mapper: mapPhoneNumbers,
    noDataLabel: translate('ADD_PHONE'),
    useMultipleInputs: true,
    Component: EditDonorPhoneInput,
    defaultValue: {
      number: '',
      label: Labels.WORK,
      primary: false,
    },
    labels: [Labels.MOBILE, Labels.WORK].map((label) => ({
      label: startCase(label),
      value: label,
    })),
    usePrimary: true,
  },
  USER_PHONE_NUMBER_OLD: {
    key: 'USER_PHONE_NUMBER',
    useMultipleInputs: true,
    defaultValue: {
      dataPath: 'phones.primary',
      type: 'primary',
    },
    types: [
      {
        label: translate('PRIMARY_LABEL'),
        value: 'primary',
      },
      {
        label: translate('MOBILE_LABEL'),
        value: 'mobile',
      },
    ],
    ...PHONE_NUMBER_COMMON_PROPS,
  },
};

const EMPLOYEE_INFO_CONFIG = [
  EMPLOYEE_PROFILE_FIELDS_CONFIG.USER_FIRST_NAME,
  EMPLOYEE_PROFILE_FIELDS_CONFIG.USER_LAST_NAME,
  EMPLOYEE_PROFILE_FIELDS_CONFIG.USER_DISPLAY_NAME,
  EMPLOYEE_PROFILE_FIELDS_CONFIG.USER_TITLE,
  EMPLOYEE_PROFILE_FIELDS_CONFIG.USER_WORK_EMAIL,
  EMPLOYEE_PROFILE_FIELDS_CONFIG.USER_PHONE_NUMBER,
];

const COMPANY_FIELDS_CONFIG = {
  ORG_NAME: {
    key: 'ORG_NAME',
    label: translate('COMPANY_NAME_LABEL'),
    path: 'orgName',
    required: true,
    validate: isNotEmpty,
  },
  EMPLOYEE_ID: {
    key: 'EMPLOYEE_ID',
    label: translate('COMPANY_EMPLOYEE_ID_LABEL'),
    path: 'ein',
    noDataLabel: translate('COMPANY_ADD_EMPLOYEE_ID_LABEL'),
    formatValue: formatEmployeeId,
    valueMapper: formatEmployeeId,
    newValueMapper: (value) =>
      value?.replace(/\D/g, '')?.slice(0, EID_NUM_LENGTH) || '',
  },
  PRIMARY_PHONE: {
    key: 'PRIMARY_PHONE',
    Component: EditDonorPhoneInput,
    label: translate('COMPANY_PHONE_NUMBER_LABEL'),
    path: 'primaryPhone',
    mapper: mapPhoneNumbers,
    noDataLabel: translate('COMPANY_ADD_PHONE_NUMBER_LABEL'),
  },
  ADDRESS: {
    key: 'ADDRESS',
    Component: AddressInputs,
    path: 'primaryAddress',
    mapper: mapAddresses,
    noDataLabel: translate('COMPANY_ADD_ADDRESS_LABEL'),
    multiline: true,
  },
  WEBSITE: {
    key: 'WEBSITE',
    label: translate('COMPANY_WEBSITE_LABEL'),
    path: 'website',
    mapper: mapWebsite,
    formatValue: formatWebAddress,
    noDataLabel: translate('COMPANY_ADD_WEBSITE_LABEL'),
    validate: isValidUrl,
    errorMessage: translate('INVALID_URL_FORMAT_MESSAGE'),
  },
};

const getCompanyInfoConfig = (isEditConfig) => {
  const config = [];

  if (isEditConfig) {
    config.push({
      key: 'ORG_NAME',
      label: translate('COMPANY_NAME_LABEL'),
      path: 'orgName',
      required: true,
      validate: isNotEmpty,
    });
  }

  return [
    ...config,
    {
      ...COMPANY_FIELDS_CONFIG.EMPLOYEE_ID,
      icon: isEditConfig ? EditEmployeeIdIcon : EmployeeIdIcon,
    },
    {
      ...COMPANY_FIELDS_CONFIG.PRIMARY_PHONE,
      icon: isEditConfig ? EditWorkPhoneIcon : WorkPhoneIcon,
    },
    {
      ...COMPANY_FIELDS_CONFIG.ADDRESS,
      icon: isEditConfig ? EditBuildingIcon : BuildingIcon,
    },
    {
      ...COMPANY_FIELDS_CONFIG.WEBSITE,
      icon: isEditConfig ? EditWebIcon : WebIcon,
    },
  ];
};

const VIEW_COMPANY_INFO_CONFIG = getCompanyInfoConfig(false);
const EDIT_COMPANY_INFO_CONFIG = getCompanyInfoConfig(true);

const USER_PROFILE_DESCRIPTOR_CONFIG_1 = [
  {
    key: 'USER_PROFILE_DESCRIPTOR_JOB_TITLE',
    icon: JobIcon,
    path: 'jobTitle',
  },
  {
    key: 'USER_WORK_EMAIL',
    icon: EmailIcon,
    path: 'workEmails',
    fieldToExtract: 'email',
    mapper: mapEmails,
  },
  {
    key: 'USER_WORK_PHONE_NUMBER',
    icon: PhoneIcon,
    path: 'mobilePhones',
    mapper: mapPhoneNumbers,
    fieldToExtract: 'number',
  },
  {
    key: 'USER_MOBILE_PHONE_NUMBER',
    icon: RedPhoneIcon,
    path: 'workPhones',
    fieldToExtract: 'number',
    mapper: mapPhoneNumbers,
  },
];

export {
  EDIT_ADDRESS_DEFAULT_VALUE,
  EMPLOYEE_INFO_CONFIG,
  VIEW_COMPANY_INFO_CONFIG,
  EDIT_COMPANY_INFO_CONFIG,
  USER_PROFILE_DESCRIPTOR_CONFIG_1,
  // DonorConnectionSection related configs
  DONOR_SECTION_CONFIGS,
};
